import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { FileText, CalendarBlank, ChartBar, Buildings, DownloadSimple, Spinner, Eye, FolderOpen } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const REPORT_TYPES = [
  { key: 'daily', label: 'Daily Review', icon: CalendarBlank, desc: 'Projects at risk, missing DPR, pending approvals, financial alerts', color: 'blue' },
  { key: 'weekly_internal', label: 'Weekly (Internal)', icon: ChartBar, desc: 'Project status, VO pending, billing gaps, procurement issues', color: 'violet' },
  { key: 'weekly_client', label: 'Weekly (Client)', icon: Buildings, desc: 'Work completed, progress summary, next week plan — no financials', color: 'emerald' },
  { key: 'monthly', label: 'Monthly Review', icon: FileText, desc: 'Financial summary, receivables, overheads, risk flags', color: 'amber' },
];

const colorMap = {
  blue: 'bg-blue-50 border-blue-200 text-blue-700',
  violet: 'bg-violet-50 border-violet-200 text-violet-700',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
};

const pdfUrlMap = {
  daily: `${BACKEND}/api/reports/daily/pdf`,
  weekly_internal: `${BACKEND}/api/reports/weekly/pdf?variant=internal`,
  weekly_client: `${BACKEND}/api/reports/weekly/pdf?variant=client`,
  monthly: `${BACKEND}/api/reports/monthly/pdf`,
};

const downloadBlob = async (url, fallbackName) => {
  const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
  const disposition = res.headers['content-disposition'] || '';
  const match = disposition.match(/filename=(.+)/);
  const fname = match ? match[1] : fallbackName;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  a.download = fname;
  a.click();
  URL.revokeObjectURL(a.href);
};

const ReportCard = ({ rtype, onGenerate, onPdf, loading, pdfLoading }) => {
  const Icon = rtype.icon;
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5 flex flex-col" data-testid={`report-${rtype.key}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-2 rounded-md border ${colorMap[rtype.color]}`}><Icon size={18} weight="fill" /></div>
        <h3 className="text-sm font-bold text-zinc-800">{rtype.label}</h3>
      </div>
      <p className="text-[11px] text-zinc-500 mb-4 flex-1">{rtype.desc}</p>
      <div className="flex gap-2">
        <Button onClick={() => onGenerate(rtype.key)} disabled={loading} size="sm" variant="outline" className="rounded-none text-xs flex-1" data-testid={`generate-${rtype.key}`}>
          {loading ? <Spinner size={14} className="animate-spin mr-1" /> : <Eye size={14} className="mr-1" />}
          View
        </Button>
        <Button onClick={() => onPdf(rtype.key)} disabled={pdfLoading} size="sm" variant="default" className="rounded-none text-xs flex-1 bg-[#1e3a5f] hover:bg-[#162d4a]" data-testid={`pdf-${rtype.key}`}>
          {pdfLoading ? <Spinner size={14} className="animate-spin mr-1" /> : <DownloadSimple size={14} className="mr-1" />}
          PDF
        </Button>
      </div>
    </div>
  );
};

const fmt = (v) => typeof v === 'number' ? v.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : v || '—';

export const ReportsCenter = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND}/api/projects`, { withCredentials: true })
      .then(r => setProjects(r.data || []))
      .catch(() => {});
  }, []);

  const generate = useCallback(async (key) => {
    setLoading(true);
    setReport(null);
    try {
      let url;
      if (key === 'weekly_internal') url = `${BACKEND}/api/reports/weekly?variant=internal`;
      else if (key === 'weekly_client') url = `${BACKEND}/api/reports/weekly?variant=client`;
      else url = `${BACKEND}/api/reports/${key}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setReport(data);
      toast.success('Report generated.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report generation failed.');
    }
    setLoading(false);
  }, []);

  const exportPdf = useCallback(async (key) => {
    setPdfLoading(key);
    try {
      const url = pdfUrlMap[key];
      await downloadBlob(url, `${key}_report.pdf`);
      toast.success('PDF downloaded.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF export failed.');
    }
    setPdfLoading(null);
  }, []);

  const exportProjectPdf = useCallback(async () => {
    if (!selectedProject) { toast.error('Select a project first.'); return; }
    setPdfLoading('project');
    try {
      await downloadBlob(`${BACKEND}/api/reports/project/${selectedProject}/pdf`, 'Project_Report.pdf');
      toast.success('Project PDF downloaded.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF export failed.');
    }
    setPdfLoading(null);
  }, [selectedProject]);

  const generateProjectReport = useCallback(async () => {
    if (!selectedProject) { toast.error('Select a project first.'); return; }
    setLoading(true);
    setReport(null);
    try {
      const { data } = await axios.get(`${BACKEND}/api/reports/project/${selectedProject}`, { withCredentials: true });
      setReport(data);
      toast.success('Project report generated.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report generation failed.');
    }
    setLoading(false);
  }, [selectedProject]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="reports-center">
      <h1 className="text-lg font-bold text-zinc-900 mb-1">Reports Center</h1>
      <p className="text-xs text-zinc-400 mb-6">Generate review reports for internal and client use</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {REPORT_TYPES.map(r => (
          <ReportCard key={r.key} rtype={r} onGenerate={generate} onPdf={exportPdf} loading={loading} pdfLoading={pdfLoading === r.key} />
        ))}
      </div>

      {/* Project Report */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-6" data-testid="project-report-section">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 rounded-md border bg-zinc-50 border-zinc-200 text-zinc-700"><FolderOpen size={18} weight="fill" /></div>
          <div>
            <h3 className="text-sm font-bold text-zinc-800">Project Report</h3>
            <p className="text-[11px] text-zinc-500">Full project status — progress, financial, VOs, billing, alerts, team</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}
            className="flex-1 border border-zinc-200 rounded-none px-3 py-2 text-xs text-zinc-700 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300"
            data-testid="project-select"
          >
            <option value="">Select project...</option>
            {projects.map(p => (
              <option key={p.id || p._id} value={p.id || p._id}>
                {p.pi_number || p.client_name} — {p.client_name}
              </option>
            ))}
          </select>
          <Button onClick={generateProjectReport} disabled={loading || !selectedProject} size="sm" variant="outline" className="rounded-none text-xs" data-testid="generate-project">
            {loading ? <Spinner size={14} className="animate-spin mr-1" /> : <Eye size={14} className="mr-1" />}
            View
          </Button>
          <Button onClick={exportProjectPdf} disabled={pdfLoading === 'project' || !selectedProject} size="sm" variant="default" className="rounded-none text-xs bg-[#1e3a5f] hover:bg-[#162d4a]" data-testid="pdf-project">
            {pdfLoading === 'project' ? <Spinner size={14} className="animate-spin mr-1" /> : <DownloadSimple size={14} className="mr-1" />}
            PDF
          </Button>
        </div>
      </div>

      {/* Report Output */}
      {report && (
        <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="report-output">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-bold text-zinc-800 capitalize">{report.report_type?.replace('_', ' ')} Report</h2>
              <p className="text-[10px] text-zinc-400">{report.date || report.period || report.month} — Generated {new Date(report.generated_at).toLocaleString()}</p>
            </div>
            <Button
              onClick={() => {
                const key = report.report_type;
                if (key === 'project') {
                  exportProjectPdf();
                } else if (key === 'weekly_internal' || key === 'weekly_client') {
                  exportPdf(key);
                } else {
                  exportPdf(key);
                }
              }}
              size="sm" variant="default" className="rounded-none text-xs bg-[#1e3a5f] hover:bg-[#162d4a]"
              data-testid="pdf-from-output"
            >
              <DownloadSimple size={14} className="mr-1" /> Export PDF
            </Button>
          </div>

          {/* Daily Report */}
          {report.report_type === 'daily' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Active Projects" value={report.active_projects} />
                <Stat label="At Risk" value={report.projects_at_risk} warn={report.projects_at_risk > 0} />
                <Stat label="Critical Alerts" value={report.financial_alerts?.critical} warn />
                <Stat label="Warnings" value={report.financial_alerts?.warnings} />
              </div>
              {report.missing_dpr?.length > 0 && (
                <Section title="Missing DPR">
                  {report.missing_dpr.map((p, i) => <div key={i} className="text-[11px] text-zinc-600 py-1 border-b border-zinc-50">{p.name}</div>)}
                </Section>
              )}
              <Section title="Pending Approvals">
                <div className="flex gap-4 text-xs text-zinc-600">
                  <span>Tasks: <b>{report.pending_approvals?.tasks}</b></span>
                  <span>Indents: <b>{report.pending_approvals?.indents}</b></span>
                  <span>VOs: <b>{report.pending_approvals?.vos}</b></span>
                </div>
              </Section>
              {report.critical_flags?.length > 0 && (
                <Section title="Critical Flags">
                  {report.critical_flags.map((f, i) => (
                    <div key={i} className="text-[11px] py-1.5 border-b border-zinc-50">
                      <span className="text-red-600 font-semibold">{f.label}</span> — <span className="text-zinc-600">{f.detail}</span>
                      <span className="text-zinc-400 ml-2">({f.project})</span>
                    </div>
                  ))}
                </Section>
              )}
            </div>
          )}

          {/* Weekly Report */}
          {report.report_type?.startsWith('weekly') && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Projects" value={report.total_projects} />
                {report.billing_gaps !== undefined && <Stat label="Billing Gaps" value={report.billing_gaps} warn={report.billing_gaps > 0} />}
                {report.pending_vos_total !== undefined && <Stat label="Pending VOs" value={report.pending_vos_total} />}
                {report.procurement_issues !== undefined && <Stat label="Procurement Issues" value={report.procurement_issues} />}
              </div>
              <Section title="Project Summaries">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-100">
                      <th className="text-left py-2 text-zinc-400 font-semibold">Project</th>
                      <th className="text-right py-2 text-zinc-400 font-semibold">DPR</th>
                      <th className="text-right py-2 text-zinc-400 font-semibold">Qty Done</th>
                      {report.report_type === 'weekly_internal' && <th className="text-right py-2 text-zinc-400 font-semibold">Billed</th>}
                      {report.report_type === 'weekly_internal' && <th className="text-right py-2 text-zinc-400 font-semibold">Alerts</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {report.projects?.map((p, i) => (
                      <tr key={i} className="border-b border-zinc-50">
                        <td className="py-2 text-zinc-700 font-medium">{p.project_name}</td>
                        <td className="py-2 text-right text-zinc-600">{p.dpr_entries_this_week}</td>
                        <td className="py-2 text-right text-zinc-600">{fmt(p.quantity_completed_this_week)}</td>
                        {p.billed_this_week !== undefined && <td className="py-2 text-right text-zinc-600">{fmt(p.billed_this_week)}</td>}
                        {p.alerts !== undefined && <td className="py-2 text-right text-zinc-600">{p.alerts}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            </div>
          )}

          {/* Monthly Report */}
          {report.report_type === 'monthly' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Projects" value={report.summary?.active_projects} />
                <Stat label="Total Billed" value={`Rs ${fmt(report.summary?.total_billed)}`} />
                <Stat label="Collected" value={`Rs ${fmt(report.summary?.total_collected)}`} />
                <Stat label="Receivable" value={`Rs ${fmt(report.summary?.total_receivable)}`} warn={report.summary?.total_receivable > 0} />
              </div>
              <Section title="Project Financials">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-100">
                      <th className="text-left py-2 text-zinc-400 font-semibold">Project</th>
                      <th className="text-right py-2 text-zinc-400 font-semibold">BOQ</th>
                      <th className="text-right py-2 text-zinc-400 font-semibold">Billed</th>
                      <th className="text-right py-2 text-zinc-400 font-semibold">Collected</th>
                      <th className="text-right py-2 text-zinc-400 font-semibold">Receivable</th>
                      <th className="text-right py-2 text-zinc-400 font-semibold">Alerts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.projects?.map((p, i) => (
                      <tr key={i} className="border-b border-zinc-50">
                        <td className="py-2 text-zinc-700 font-medium">{p.project_name}</td>
                        <td className="py-2 text-right text-zinc-600">{fmt(p.boq_value)}</td>
                        <td className="py-2 text-right text-zinc-600">{fmt(p.total_billed)}</td>
                        <td className="py-2 text-right text-zinc-600">{fmt(p.total_collected)}</td>
                        <td className="py-2 text-right font-semibold text-amber-700">{fmt(p.receivable)}</td>
                        <td className="py-2 text-right text-zinc-600">{p.active_alerts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            </div>
          )}

          {/* Project Report */}
          {report.report_type === 'project' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="PI Number" value={report.project?.pi_number} />
                <Stat label="Status" value={report.project?.status} />
                <Stat label="BOQ Value" value={`Rs ${fmt(report.progress?.boq_value)}`} />
                <Stat label="Receivable" value={`Rs ${fmt(report.financial?.receivable)}`} warn={report.financial?.receivable > 0} />
              </div>
              <Section title="Team">
                <div className="flex gap-6 text-xs text-zinc-600">
                  <span>PM: <b>{report.project?.team?.pm || '—'}</b></span>
                  <span>Lead: <b>{report.project?.team?.lead || '—'}</b></span>
                  <span>Accounts: <b>{report.project?.team?.accounts || '—'}</b></span>
                </div>
              </Section>
              <Section title="Financial Summary">
                <table className="w-full text-xs">
                  <tbody>
                    {[
                      ['BOQ Value', report.financial?.boq_value],
                      ['Approved VO Value', report.financial?.vo_approved_value],
                      ['Revised Contract', report.financial?.revised_contract],
                      ['Total Billed', report.financial?.total_billed],
                      ['Total Invoiced', report.financial?.total_invoiced],
                      ['Collected', report.financial?.total_collected],
                      ['Receivable', report.financial?.receivable],
                    ].map(([k, v], i) => (
                      <tr key={i} className="border-b border-zinc-50">
                        <td className="py-1.5 text-zinc-500 w-48">{k}</td>
                        <td className={`py-1.5 text-right font-medium ${k === 'Receivable' ? 'text-amber-700' : 'text-zinc-800'}`}>Rs {fmt(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
              <Section title="Variation Orders">
                <div className="flex gap-6 text-xs text-zinc-600">
                  <span>Total: <b>{report.variation_orders?.total}</b></span>
                  <span>Approved: <b>{report.variation_orders?.approved}</b></span>
                  <span>Pending: <b>{report.variation_orders?.pending}</b></span>
                  <span>Value: <b>Rs {fmt(report.variation_orders?.approved_value)}</b></span>
                </div>
              </Section>
              {report.alerts?.length > 0 && (
                <Section title="Active Alerts">
                  {report.alerts.map((a, i) => (
                    <div key={i} className={`text-[11px] py-1.5 px-2 mb-1 rounded-sm ${a.severity === 'critical' ? 'bg-red-50 text-red-700' : a.severity === 'warning' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                      <span className="font-semibold">{a.label}</span> — {a.detail}
                    </div>
                  ))}
                </Section>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, warn }) => (
  <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
    <div className="text-[10px] text-zinc-400 uppercase font-semibold">{label}</div>
    <div className={`text-lg font-bold mt-0.5 ${warn ? 'text-red-600' : 'text-zinc-800'}`}>{value ?? 0}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-bold text-zinc-600 uppercase tracking-wide mb-2">{title}</h3>
    {children}
  </div>
);

export default ReportsCenter;
